'use client'

/**
 * EChartsCashFlow — reusable ECharts wrapper for cash flow charts.
 *
 * Handles:
 * - Lazy init/dispose lifecycle
 * - Dark mode detection via MutationObserver on <html> class
 * - ResizeObserver for responsive sizing
 * - Efficient option updates (setOption without re-init)
 * - Click event forwarding to parent
 * - Scroll passthrough (never hijacks page scroll)
 * - Ctrl+scroll / pinch-to-zoom on chart
 * - Drag to pan, grab/grabbing cursor
 * - Reset button overlay
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'

import type { DataZoomComponentOption } from 'echarts/components'

import { useIsomorphicLayoutEffect } from '@/hooks/common/useIsomorphicLayoutEffect'

import type { ECharts, EChartsOption } from './echarts-setup'
import { getECharts } from './echarts-setup'

/** Normalize the option's dataZoom field to a flat array. */
function toDataZoomArray(
  dz: EChartsOption['dataZoom'],
): DataZoomComponentOption[] {
  if (Array.isArray(dz)) return dz
  return dz ? [dz] : []
}

interface ZoomWindow {
  startValue: number
  endValue: number
}

/** Scan the option's series arrays to find the min/max timestamp (x value). */
function getSeriesExtent(option: EChartsOption): ZoomWindow | null {
  const series = Array.isArray(option.series) ? option.series : []
  let min = Infinity
  let max = -Infinity
  for (const s of series) {
    const data = (s as { data?: [number, number][] }).data
    if (!Array.isArray(data)) continue
    for (const d of data) {
      if (!Array.isArray(d)) continue
      const x = d[0]
      if (typeof x === 'number') {
        if (x < min) min = x
        if (x > max) max = x
      }
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null
  return { startValue: min, endValue: max }
}

/**
 * Resolve a dataZoom config entry to absolute timestamp values.
 * Handles both `startValue/endValue` (FY mode) and `start/end` percentage
 * (all-time mode) by converting percentages using the series data extent.
 */
function resolveDataZoomPosition(
  dz: DataZoomComponentOption | undefined,
  option: EChartsOption,
): ZoomWindow | null {
  if (dz?.startValue != null && dz.endValue != null) {
    return { startValue: Number(dz.startValue), endValue: Number(dz.endValue) }
  }
  const extent = getSeriesExtent(option)
  if (!extent) return null
  const range = extent.endValue - extent.startValue
  const startPct = dz?.start ?? 0
  const endPct = dz?.end ?? 100
  return {
    startValue: extent.startValue + (startPct / 100) * range,
    endValue: extent.startValue + (endPct / 100) * range,
  }
}

interface EChartsCashFlowProps {
  option: EChartsOption
  /** Fixed pixel height, or omit to fill parent container */
  height?: number
  onClick?: (params: {
    seriesName?: string
    dataIndex?: number
    name?: string
  }) => void
  className?: string
  /** When this value changes, the chart does a full reset (notMerge) instead of
   *  a partial update — clearing dataZoom position, axes, etc. Use this when
   *  switching between fundamentally different views (e.g. different awards). */
  resetKey?: string | number
  /** When current is true, ResizeObserver defers chart.resize() — the canvas
   *  repaint is invisible behind the VT screenshot overlay anyway. A trailing
   *  call fires after the animation to apply the final size. */
  deferResize?: React.RefObject<boolean>
  /** External zoom override via dispatchAction (animates like a user drag).
   *  When non-null, the option's dataZoom position is stripped from setOption
   *  to prevent it from fighting with the dispatch. When null, the option's
   *  own dataZoom takes effect. */
  zoomWindow?: ZoomWindow | null
  /** Show a reset-zoom button overlay (typically when expanded) */
  showResetButton?: boolean
}

const RESET_BUTTON_CLASS =
  'absolute bottom-9 right-2 z-10 flex h-6 items-center rounded border border-slate-200/60 bg-white/80 px-1.5 text-[10px] font-medium text-slate-500 backdrop-blur-sm transition-colors hover:border-slate-300 hover:bg-white hover:text-slate-700 dark:border-zinc-600/60 dark:bg-zinc-800/80 dark:text-zinc-400 dark:hover:border-zinc-500 dark:hover:bg-zinc-800 dark:hover:text-zinc-200'

export const EChartsCashFlow = React.memo(function EChartsCashFlow({
  option,
  height,
  onClick,
  className,
  resetKey,
  deferResize,
  zoomWindow,
  showResetButton,
}: EChartsCashFlowProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<ECharts | null>(null)
  const onClickRef = useRef(onClick)
  // Always tracks the latest option so the async init callback never applies
  // a stale closure value.
  const optionRef = useRef(option)
  optionRef.current = option
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined'
      ? document.documentElement.classList.contains('dark')
      : false,
  )

  // Keep ref in sync so the init closure always calls the latest callback
  onClickRef.current = onClick

  // Track zoomWindow in a ref so the layout effect can read it without
  // adding it as a dependency (we don't want option setOption to re-fire
  // when only the zoom changes).
  const zoomWindowRef = useRef(zoomWindow)
  zoomWindowRef.current = zoomWindow

  // Self-tracked zoom position — avoids reading from chart.getOption() which
  // returns unreliable values (sometimes percentages instead of timestamps),
  // causing the animation to briefly go the wrong direction.
  const zoomPosRef = useRef<ZoomWindow | null>(null)
  const zoomAnimRef = useRef<number | null>(null)
  const isAnimatingRef = useRef(false)
  const wheelCleanupRef = useRef<(() => void) | null>(null)

  // Track whether zoom has been changed from default (for reset button visibility).
  // Updates are debounced to avoid rapid re-renders from floating-point boundary
  // noise in datazoom events (e.g. 0.4999% vs 0.5001% toggling the threshold).
  const [isZoomed, setIsZoomed] = useState(false)
  const zoomDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const deferResizeRef = useRef(deferResize)
  deferResizeRef.current = deferResize

  // Detect dark mode
  useEffect(() => {
    const checkDark = () => {
      setIsDark(document.documentElement.classList.contains('dark'))
    }
    checkDark()
    const observer = new MutationObserver(checkDark)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })
    return () => observer.disconnect()
  }, [])

  // Init/dispose lifecycle — re-init when dark mode changes (theme requires re-init)
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let cancelled = false

    // Dispose previous instance
    if (chartRef.current) {
      chartRef.current.dispose()
      chartRef.current = null
    }

    void getECharts().then(({ init }) => {
      if (cancelled || !containerRef.current) return

      const chart = init(
        containerRef.current,
        isDark ? 'sweetspot-dark' : undefined,
        { renderer: 'canvas' },
      )
      chartRef.current = chart

      // ECharts' ZRender attaches a non-passive wheel listener that calls
      // preventDefault(), blocking page scroll even when zoomOnMouseWheel and
      // moveOnMouseWheel are both false. Intercept wheel events in the capture
      // phase before ZRender sees them so the page can scroll normally.
      // stopImmediatePropagation prevents ECharts from receiving the event;
      // not calling preventDefault allows the browser to scroll.
      const wheelPassthrough = (e: WheelEvent) => {
        e.stopImmediatePropagation()
      }
      const wheelEl = containerRef.current
      wheelEl?.addEventListener('wheel', wheelPassthrough, {
        capture: true,
        passive: true,
      })
      wheelCleanupRef.current = () =>
        wheelEl?.removeEventListener('wheel', wheelPassthrough, {
          capture: true,
        })

      // Apply the LATEST option via ref — the `option` closure value is stale
      // when async queries resolve between effect creation and this callback.
      chart.setOption(optionRef.current, { notMerge: true, lazyUpdate: true })

      // Apply initial zoom window if one is already set at mount time
      if (zoomWindowRef.current) {
        chart.dispatchAction({
          type: 'dataZoom',
          startValue: zoomWindowRef.current.startValue,
          endValue: zoomWindowRef.current.endValue,
        })
      }

      // Track manual zoom (user drag/scroll) so zoomPosRef stays in sync.
      // Skip events fired by our own animation dispatches.
      chart.on('datazoom', (params: unknown) => {
        if (isAnimatingRef.current) return
        const p = params as {
          start?: number
          end?: number
          batch?: { start?: number; end?: number }[]
        }
        const startPct = p.batch?.[0]?.start ?? p.start
        const endPct = p.batch?.[0]?.end ?? p.end
        if (startPct == null || endPct == null) return
        const extent = getSeriesExtent(optionRef.current)
        if (!extent) return
        const range = extent.endValue - extent.startValue
        zoomPosRef.current = {
          startValue: extent.startValue + (startPct / 100) * range,
          endValue: extent.startValue + (endPct / 100) * range,
        }
        // Consider zoomed if not at full range (with 0.5% tolerance).
        // Debounce to prevent rapid re-renders from boundary noise.
        const zoomed = !(startPct < 0.5 && endPct > 99.5)
        if (zoomDebounceRef.current != null)
          clearTimeout(zoomDebounceRef.current)
        zoomDebounceRef.current = setTimeout(() => setIsZoomed(zoomed), 100)
      })

      // Forward click events
      chart.on('click', (params: unknown) => {
        onClickRef.current?.(
          params as { seriesName?: string; dataIndex?: number; name?: string },
        )
      })
    })

    return () => {
      cancelled = true
      wheelCleanupRef.current?.()
      wheelCleanupRef.current = null
      if (zoomDebounceRef.current != null) clearTimeout(zoomDebounceRef.current)
      if (chartRef.current) {
        chartRef.current.dispose()
        chartRef.current = null
      }
    }
    // Re-init on dark mode change — option updates handled separately below
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isDark])

  // Update option without re-init.
  // replaceMerge: ['series'] replaces the series array in full (no stale series left
  // behind when switching chart types) while preserving all other chart state —
  // most importantly the dataZoom position so the user doesn't lose their scroll.
  //
  // When resetKey changes, use notMerge to fully reset the chart (axes, zoom, etc.)
  // so switching between different awards/views doesn't inherit stale zoom state.
  // We track both the key and the option reference at the time of the key change
  // so that notMerge fires on every option update until a genuinely new option
  // arrives (i.e. the async-loaded data, not the frozen stale one).
  const prevResetKeyRef = useRef(resetKey)
  const optionAtResetRef = useRef(option)
  const pendingResetRef = useRef(false)
  // useLayoutEffect so this runs synchronously during flushSync — before
  // the View Transitions API captures the "new" screenshot. With useEffect
  // the canvas would still show old bars in the VT snapshot.
  useIsomorphicLayoutEffect(() => {
    const chart = chartRef.current
    if (!chart || chart.isDisposed()) return
    if (resetKey !== prevResetKeyRef.current) {
      prevResetKeyRef.current = resetKey
      optionAtResetRef.current = option
      pendingResetRef.current = true
    }
    // During a view transition the chart is behind the VT screenshot overlay —
    // suppress ECharts' own animation so it doesn't compete with the VT morph,
    // and use lazyUpdate:false to force synchronous canvas rendering so the
    // updated bars appear in the VT snapshot.
    const transitioning = deferResizeRef.current?.current
    let effectiveOption: EChartsOption = transitioning
      ? { ...option, animation: false }
      : option

    // When an external zoomWindow is active, strip zoom position values from
    // the option so setOption doesn't snap the zoom and fight with the
    // dispatchAction that controls it.
    if (zoomWindowRef.current && !pendingResetRef.current) {
      const zoomArr = toDataZoomArray(effectiveOption.dataZoom)
      if (zoomArr.length > 0) {
        const stripped = zoomArr.map(
          ({ start, end, startValue, endValue, ...rest }) => rest,
        )
        effectiveOption =
          effectiveOption === option
            ? { ...option, dataZoom: stripped }
            : { ...effectiveOption, dataZoom: stripped }
      }
    }

    const lazy = !transitioning
    if (pendingResetRef.current) {
      // Keep applying notMerge until a new option object arrives
      // (the first fire often hits the frozen/stale option during loading)
      if (option !== optionAtResetRef.current) {
        pendingResetRef.current = false
      }
      chart.setOption(effectiveOption, { notMerge: true, lazyUpdate: lazy })
      // After notMerge reset, re-apply the active zoom window so time range
      // buttons (3M, 6M, etc.) stay in sync across drill transitions.
      // Clear tracked position so the next zoom animation starts fresh.
      zoomPosRef.current = null
      if (zoomWindowRef.current) {
        chart.dispatchAction({
          type: 'dataZoom',
          startValue: zoomWindowRef.current.startValue,
          endValue: zoomWindowRef.current.endValue,
        })
        zoomPosRef.current = {
          startValue: zoomWindowRef.current.startValue,
          endValue: zoomWindowRef.current.endValue,
        }
      }
    } else {
      chart.setOption(effectiveOption, {
        replaceMerge: ['series', 'yAxis', 'dataZoom'],
        lazyUpdate: lazy,
      })
    }
    // Force synchronous canvas paint so VT captures the updated chart
    if (transitioning) {
      chart.resize()
    }
  }, [option, resetKey])

  const zoomStart = zoomWindow?.startValue ?? null
  const zoomEnd = zoomWindow?.endValue ?? null
  useEffect(() => {
    const chart = chartRef.current
    if (!chart || chart.isDisposed()) return

    // Cancel any in-progress animation
    if (zoomAnimRef.current != null) cancelAnimationFrame(zoomAnimRef.current)

    // Resolve target zoom
    const opt = optionRef.current
    let targetStart: number
    let targetEnd: number
    if (zoomStart != null && zoomEnd != null) {
      targetStart = zoomStart
      targetEnd = zoomEnd
    } else {
      // Deselected — animate back to the option's default dataZoom range.
      // Handles both absolute (FY mode) and percentage (all-time) configs.
      const resolved = resolveDataZoomPosition(
        toDataZoomArray(opt.dataZoom)[0],
        opt,
      )
      if (resolved) {
        targetStart = resolved.startValue
        targetEnd = resolved.endValue
      } else {
        zoomPosRef.current = null
        return
      }
    }

    // Use our self-tracked position as the "from". If we don't have one
    // (first animation or after manual zoom reset), seed from the option's
    // dataZoom config — converting percentages to absolute values via the
    // series data extent.
    let from = zoomPosRef.current
    if (!from) {
      from = resolveDataZoomPosition(toDataZoomArray(opt.dataZoom)[0], opt)
      if (from) {
        zoomPosRef.current = from
      } else {
        // Can't determine a "from" — snap and track for next time
        chart.dispatchAction({
          type: 'dataZoom',
          startValue: targetStart,
          endValue: targetEnd,
        })
        zoomPosRef.current = { startValue: targetStart, endValue: targetEnd }
        return
      }
    }

    const fromStart = from.startValue
    const fromEnd = from.endValue

    const DURATION = 400
    const startTime = performance.now()

    // Cubic ease-out: fast start, gentle deceleration
    const easeOut = (t: number) => 1 - (1 - t) ** 3

    isAnimatingRef.current = true

    const tick = (now: number) => {
      if (!chartRef.current || chartRef.current.isDisposed()) {
        isAnimatingRef.current = false
        return
      }
      const elapsed = now - startTime
      const progress = Math.min(elapsed / DURATION, 1)
      const ease = easeOut(progress)

      const s = fromStart + (targetStart - fromStart) * ease
      const e = fromEnd + (targetEnd - fromEnd) * ease

      // Track the current interpolated position
      zoomPosRef.current = { startValue: s, endValue: e }

      chartRef.current.dispatchAction({
        type: 'dataZoom',
        startValue: s,
        endValue: e,
      })

      if (progress < 1) {
        zoomAnimRef.current = requestAnimationFrame(tick)
      } else {
        zoomAnimRef.current = null
        isAnimatingRef.current = false
        // datazoom events were suppressed during animation (isAnimatingRef was true),
        // so manually update isZoomed now that the animation has settled.
        const extent = getSeriesExtent(optionRef.current)
        if (extent && extent.endValue > extent.startValue) {
          const range = extent.endValue - extent.startValue
          const sPct = ((s - extent.startValue) / range) * 100
          const ePct = ((e - extent.startValue) / range) * 100
          setIsZoomed(!(sPct < 0.5 && ePct > 99.5))
        }
      }
    }

    zoomAnimRef.current = requestAnimationFrame(tick)

    return () => {
      if (zoomAnimRef.current != null) cancelAnimationFrame(zoomAnimRef.current)
      isAnimatingRef.current = false
    }
  }, [zoomStart, zoomEnd])

  // ResizeObserver — defers during view transitions (canvas repaint is
  // invisible behind the VT overlay), otherwise throttled at 150ms with
  // a guaranteed trailing call for the final size.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let timeout: ReturnType<typeof setTimeout> | null = null
    let lastCall = 0

    const doResize = () => {
      lastCall = Date.now()
      if (chartRef.current && !chartRef.current.isDisposed()) {
        chartRef.current.resize()
      }
    }

    const resizeObserver = new ResizeObserver(() => {
      // During a view transition the chart is hidden behind the VT
      // screenshot overlay — defer the expensive resize + canvas repaint
      // until after the animation finishes (~300ms trailing call).
      if (deferResizeRef.current?.current) {
        if (timeout != null) clearTimeout(timeout)
        timeout = setTimeout(doResize, 320)
        return
      }

      const now = Date.now()
      const elapsed = now - lastCall
      if (elapsed >= 150) {
        doResize()
      } else {
        if (timeout != null) clearTimeout(timeout)
        timeout = setTimeout(doResize, 150 - elapsed)
      }
    })
    resizeObserver.observe(container)
    return () => {
      resizeObserver.disconnect()
      if (timeout != null) clearTimeout(timeout)
    }
  }, [])

  // ─── Reset zoom ─────────────────────────────────────────────────
  const resetZoom = useCallback(() => {
    const chart = chartRef.current
    if (!chart || chart.isDisposed()) return
    chart.dispatchAction({ type: 'dataZoom', start: 0, end: 100 })
    setIsZoomed(false)
  }, [])

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        ...(height != null ? { height } : { position: 'absolute', inset: 0 }),
      }}
    >
      <div
        ref={containerRef}
        style={{
          position: 'absolute',
          inset: 0,
          touchAction: 'pan-y',
        }}
      />
      {showResetButton && isZoomed && (
        <button
          type="button"
          onClick={resetZoom}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Reset zoom"
          className={RESET_BUTTON_CLASS}
        >
          Reset
        </button>
      )}
    </div>
  )
})
